import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/common/Header';
import Button from '../../components/common/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiTarget, FiAward, FiGlobe, FiHeart, FiCamera } = FiIcons;

const AboutPage = () => {
  const team = [
    {
      name: 'Alex Rodriguez',
      role: 'Founder & CEO',
      bio: 'Former professional photographer with 15+ years of experience, Alex founded Tasveer to solve the challenges he faced in his own photography business.',
      avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&crop=face'
    },
    {
      name: 'Sarah Chen',
      role: 'Chief Product Officer',
      bio: 'With a background in UX design and software development, Sarah leads our product team to create intuitive tools for photographers.',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop&crop=face'
    },
    {
      name: 'Michael Johnson',
      role: 'CTO',
      bio: 'Michael brings 20+ years of experience in cloud infrastructure and AI to lead our engineering team in building scalable, cutting-edge solutions.',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face'
    },
    {
      name: 'Priya Patel',
      role: 'Head of Customer Success',
      bio: 'As a former wedding photographer, Priya understands the challenges photographers face and helps our customers succeed with Tasveer.',
      avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&h=200&fit=crop&crop=face'
    }
  ];

  const values = [
    {
      icon: FiCamera,
      title: 'Photographer First',
      description: "We build every feature with photographers in mind, focusing on what makes their workflow easier and more efficient."
    },
    {
      icon: FiHeart,
      title: 'Passion for Quality',
      description: "We're obsessed with creating the highest quality tools and experiences for both photographers and their clients."
    },
    {
      icon: FiUsers,
      title: 'Community Driven',
      description: "We actively listen to our community of photographers and incorporate their feedback into our product roadmap."
    },
    {
      icon: FiTarget,
      title: 'Focused Innovation',
      description: "We constantly innovate and push the boundaries of what's possible in photography technology."
    },
    {
      icon: FiGlobe,
      title: 'Global Perspective',
      description: "We embrace diversity and build our platform to serve photographers from all backgrounds and regions."
    },
    {
      icon: FiAward,
      title: 'Excellence in Everything',
      description: "From customer support to code quality, we strive for excellence in everything we do."
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-dark-900">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900/10 dark:to-secondary-900/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6"
          >
            About Tasveer
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
          >
            We're on a mission to empower photographers worldwide with innovative tools and technology.
          </motion.p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:w-1/2"
            >
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-600 dark:text-gray-300">
                <p>
                  Founded in 2020 by professional photographer Alex Rodriguez, Tasveer was born from frustration with the existing tools for managing client galleries and photography business workflows.
                </p>
                <p>
                  After spending countless hours managing multiple platforms for gallery hosting, client management, and sales, Alex envisioned a unified solution that would streamline the entire process for photographers.
                </p>
                <p>
                  What started as a simple gallery hosting platform has evolved into a comprehensive suite of tools for photographers of all specialties. Today, Tasveer serves over 50,000 photographers in more than 150 countries.
                </p>
                <p>
                  Our team has grown from a solo founder to a diverse group of engineers, designers, and photography enthusiasts dedicated to building the best platform for photographers worldwide.
                </p>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:w-1/2"
            >
              <div className="relative">
                <div className="aspect-video bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1452587925148-ce544e77e70d?w=800&h=600&fit=crop" 
                    alt="Photographer at work" 
                    className="w-full h-full object-cover mix-blend-overlay" 
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-white dark:bg-dark-800 rounded-2xl p-4 shadow-xl">
                  <img 
                    src="https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=400&h=400&fit=crop" 
                    alt="Photography equipment" 
                    className="w-full h-full object-cover rounded-xl" 
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50 dark:bg-dark-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              These principles guide everything we do at Tasveer.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div 
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-dark-700 rounded-xl p-8 shadow-sm border border-gray-200 dark:border-gray-700"
              >
                <div className="w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-6">
                  <SafeIcon icon={value.icon} className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Team */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              The passionate people behind Tasveer.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div 
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-dark-800 rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700"
              >
                <div className="aspect-square">
                  <img 
                    src={member.avatar} 
                    alt={member.name} 
                    className="w-full h-full object-cover" 
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    {member.name}
                  </h3>
                  <p className="text-primary-600 dark:text-primary-400 font-medium mb-3">
                    {member.role}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {member.bio}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              We're growing! Interested in joining our team?
            </p>
            <Button variant="outline">
              View Open Positions
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Join Us in Transforming Photography
          </h2>
          <p className="text-xl opacity-90 mb-8">
            Experience the platform built by photographers, for photographers.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/signup">
              <Button 
                variant="secondary" 
                size="xl" 
                className="bg-white text-primary-600 hover:bg-gray-100"
              >
                Start Free Trial
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                variant="outline" 
                size="xl" 
                className="border-white text-white hover:bg-white hover:text-primary-600"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;